import type { GeoLocation } from '@openx/types/geoLocation.ts';
import { type ChangeEvent, type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_LATITUDE, MAX_LONGITUDE, MIN_LATITUDE, MIN_LONGITUDE } from '../utils/constants.ts';
import { splitCoordinates } from '../utils/splitCoordinates.ts';
import { validateCoordinates } from '../utils/validateCoordinates.ts';

export const useCoordinates = () => {
  const { t } = useTranslation();
  const [touchedCoordinates, setTouchedCoordinates] = useState(false);
  const [coordinates, setCoordinates] = useState('');
  const [coordinatesError, setCoordinatesError] = useState<null | string | ReactNode>(null);

  const clearErrors = useCallback(() => {
    setCoordinatesError(null);
  }, []);

  const onCoordinatesChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCoordinates(event.target.value);
      clearErrors();
    },
    [clearErrors]
  );

  const onCoordinatesBlur = useCallback(() => {
    setTouchedCoordinates(true);
    const [lat, lon, ...extraValues] = splitCoordinates(coordinates);

    const isCoordinatesError = !validateCoordinates(lat, lon, extraValues);

    setCoordinatesError(
      isCoordinatesError ? (
        <>
          {t('Latitude: Allowed range: {MIN_LATITUDE}–{MAX_LATITUDE} (6 decimals)', { MIN_LATITUDE, MAX_LATITUDE })}
          <br />
          {t('Longitude: Allowed range: {MIN_LONGITUDE}–{MAX_LONGITUDE} (6 decimals)', {
            MIN_LONGITUDE,
            MAX_LONGITUDE,
          })}
        </>
      ) : null
    );
  }, [coordinates, t]);

  const onCoordinatesSelect = useCallback(
    (item: GeoLocation) => {
      setTouchedCoordinates(true);
      setCoordinates(`${item.latitude},${item.longitude}`);
      clearErrors();
    },
    [clearErrors]
  );

  return {
    coordinates,
    setCoordinates,
    touchedCoordinates,
    setTouchedCoordinates,
    coordinatesError,
    setCoordinatesError,
    onCoordinatesChange,
    onCoordinatesBlur,
    onCoordinatesSelect,
    clearErrors,
  };
};
